import { Fragment, forwardRef, useEffect, useRef, useState } from 'react';
import { produce } from 'immer';
import DaumPostcodeEmbed from 'react-daum-postcode';

import { useBoundStore } from 'app/store';
import { FormInput } from 'features/HsptlzForm/ui';
import { withNamespace } from 'shared/lib';
import { lazyFn } from 'shared/lib/utils';
import { Checkbox01 } from 'shared/ui/Checkbox';

import { useUserFormContext } from '../model';

const Field = ({ children }) => {
  return (
    <Fragment>
      <div className='patient-info-form'>{children}</div>
    </Fragment>
  );
};

const NameAndBirth = ({ usrNm, usrBirthday }) => {
  return (
    <div className='info-item'>
      <h3 className='title'>환자 정보</h3>
      <div className='content'>
        <span className='text'>
          {usrNm} / {usrBirthday}
        </span>
      </div>
    </div>
  );
};

const TelContainer = forwardRef(({ phoneInputDataRef, msgRcvTelMaxNum }, ref) => {
  const { invalidFields, setInvalidFields } = useUserFormContext();
  const { usrTelNo } = useBoundStore((state) => state.hsptlzForm.patientInfoData);
  const [telList, setTelList] = useState(usrTelNo?.split('|') || ['']);
  const isRemove = useRef(false);

  useEffect(() => {
    if (isRemove.current) {
      isRemove.current = false;

      telList.forEach((tel, index) => {
        if (tel.length >= 10 && tel.length <= 11 && tel.at(0) === '0') {
          setInvalidFields(
            produce((draft) => {
              draft.tel[index] = false;
            }),
          );
        } else
          setInvalidFields(
            produce((draft) => {
              draft.tel[index] = true;
            }),
          );
      });
    }

    phoneInputDataRef.current = telList.join('|');
  }, [telList]);

  const onBlurTel = (e, index) => {
    setTelList(
      produce((draft) => {
        draft[index] = e.target.value;
      }),
    );

    if (isRemove.current) return;

    if (e.target.value.length >= 10 && e.target.value.length <= 11 && e.target.value.at(0) === '0') {
      const newTel = invalidFields.tel.map((tel, i) => (i === index ? false : tel));
      setInvalidFields({ ...invalidFields, tel: newTel });
    } else {
      const newTel = invalidFields.tel.map((tel, i) => (i === index ? true : tel));
      setInvalidFields({ ...invalidFields, tel: newTel });
    }
  };

  const addTel = () => {
    setTelList([...telList, '']);
    setInvalidFields(
      produce((draft) => {
        draft.tel.push(undefined);
      }),
    );
  };

  const onRemoveTel = (index) => {
    setInvalidFields(
      produce((draft) => {
        draft.tel.splice(index, 1);
      }),
    );
    setTelList(
      produce((draft) => {
        draft.splice(index, 1);
      }),
    );
  };

  return (
    <div className='info-item'>
      <h3 className='title'>연락처</h3>
      <div className='item item-tel contact-input-container'>
        <div className='contact-input-wrap'>
          {telList.map((value, index) => (
            <Tel
              key={index + value}
              index={index}
              value={value}
              invalid={invalidFields.tel[index]}
              onBlurTel={onBlurTel}
              onRemoveTel={onRemoveTel}
              isMultiple={msgRcvTelMaxNum > 1}
              canRemove={telList.length > 1}
              isRemove={isRemove}
              ref={(el) => (ref.current[index] = el)}
            />
          ))}
        </div>
        {telList.length < Number(msgRcvTelMaxNum) && (
          <div className='contact-input-add'>
            <button type='button' onMouseDown={addTel} className='contact-input-add-btn'>
              <span>연락처 추가</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
});
TelContainer.displayName = 'TelContainer';

const Tel = forwardRef(({ index, value, invalid, isMultiple, canRemove, isRemove, onBlurTel, onRemoveTel }, ref) => {
  const [tel, setTel] = useState(value);

  return (
    <div className='contact-input-item'>
      {isMultiple && <div className='contact-input-label'>연락처 {index + 1}</div>}
      <div className='contact-input-field'>
        <FormInput
          type='text'
          placeholder='예) 01012345678'
          name='tel'
          className={`inp-text-01 input-name`}
          inputMode='numeric'
          minLength={10}
          maxLength={11}
          value={tel}
          invalid={invalid}
          onChange={(e) => setTel(e.target.value)}
          onBlur={(e) => onBlurTel(e, index)}
          ref={ref}
        />
      </div>
      {canRemove && (
        <button
          className='contact-remove-btn'
          onMouseDown={() => {
            isRemove.current = true;
            lazyFn(onRemoveTel, index);
          }}
        >
          <span>삭제</span>
        </button>
      )}
    </div>
  );
});
Tel.displayName = 'Tel';

const AddressContainer = () => {
  const { addressData, setAddressData } = useUserFormContext();

  // 주소 상태
  const [showPostcode, setShowPostcode] = useState(false);
  const addrSectionRef = useRef(null);
  const postcodeRef = useRef(null);
  const [actualAddrKey, setActualAddrKey] = useState('actualAddr');

  // 실제 거주지 주소 상태
  const [showActualPostcode, setShowActualPostcode] = useState(false);
  const actualAddrSectionRef = useRef(null);
  const actualPostcodeRef = useRef(null);

  const handleClickOutside = (postCode, ShowPostcode, postcodeRef) => {
    if (!postCode) return;

    function onClickOutside(e) {
      if (postcodeRef.current && !postcodeRef.current.contains(e.target)) {
        setShowPostcode(false);
      }
    }

    document.addEventListener('mousedown', onClickOutside);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
    };
  };

  useEffect(() => {
    handleClickOutside(showPostcode, setShowPostcode, postcodeRef);
  }, [showPostcode, setShowPostcode, postcodeRef]);

  useEffect(() => {
    handleClickOutside(showActualPostcode, setShowActualPostcode, actualPostcodeRef);
  }, [showActualPostcode, setShowActualPostcode, actualPostcodeRef]);

  const onClickAddress = (showPostcode, addrSectionRef) => {
    showPostcode(true);
    setTimeout(() => {
      if (addrSectionRef.current) {
        const rect = addrSectionRef.current.getBoundingClientRect();
        window.scrollTo({ top: window.scrollY + rect.top - 100, behavior: 'smooth' });
      }
    }, 100);
  };

  const onChangeAddress = (e, type) => {
    setAddressData(
      produce((draft) => {
        draft[type].dtlAddr = e.target.value;
      }),
    );
  };

  return (
    <>
      <Address
        title='주소'
        name='addr'
        postcodeRef={postcodeRef}
        addrSectionRef={addrSectionRef}
        showPostcode={showPostcode}
        setShowPostcode={setShowPostcode}
        onClickAddress={onClickAddress}
        onChangeAddress={onChangeAddress}
      >
        <div
          className='address-confirm'
          onClick={() => {
            setAddressData(
              produce((draft) => {
                draft.actualAddr.isSameAddr = !draft.actualAddr.isSameAddr;
              }),
            );
            setActualAddrKey(actualAddrKey === 'actualAddr' ? 'addr' : 'actualAddr');
          }}
        >
          {/* <Checkbox01 checked={addressData.actualAddr.isSameAddr} />
          <span className='address-confirm-txt'>입력한 주소와 실제 거주지 주소가 동일해요.</span> */}
        </div>
      </Address>
      {/* <Address
        title='실제 거주지 주소'
        name={actualAddrKey}
        postcodeRef={actualPostcodeRef}
        addrSectionRef={actualAddrSectionRef}
        showPostcode={showActualPostcode}
        setShowPostcode={setShowActualPostcode}
        onClickAddress={onClickAddress}
        onChangeAddress={onChangeAddress}
      /> */}
    </>
  );
};

const BasicAddress = ({ addrSectionRef, onClickAddress, name }) => {
  const { addressData } = useUserFormContext();

  return (
    <div className='item item-address address-input-container' ref={addrSectionRef}>
      <FormInput
        type='text'
        placeholder='주소를 검색해 주세요'
        name='userNm'
        className='inp-text-01 input-name'
        value={addressData[name].bscAddr && addressData[name].bscAddr !== 'null' ? addressData[name].bscAddr : ''}
        readOnly
        onClick={onClickAddress}
      />
    </div>
  );
};

const DetailAddress = ({ name, onChangeAddress }) => {
  const { addressData } = useUserFormContext();
  const dtlAddrInputRef = useRef(null);

  return (
    <FormInput
      type='text'
      placeholder='상세주소를 입력해 주세요'
      name={name}
      className='inp-text-01 input-name'
      value={addressData[name].dtlAddr && addressData[name].dtlAddr !== 'null' ? addressData[name].dtlAddr : ''}
      onChange={(e) => onChangeAddress(e, name)}
      ref={dtlAddrInputRef}
    />
  );
};

const DaumPostcode = ({ showPostcode, setShowPostcode, postcodeRef, name }) => {
  const { setAddressData } = useUserFormContext();
  return (
    showPostcode && (
      <div key={showPostcode} className='icon-close-wrap' ref={postcodeRef}>
        <button className='icon-close' onClick={() => setShowPostcode(false)}>
          닫기
        </button>

        <DaumPostcodeEmbed
          onComplete={({ address, buildingName, buildingCode, zonecode }) => {
            setAddressData(
              produce((draft) => {
                draft[name].postNo = zonecode;
                draft[name].bscAddr = `${address} ${buildingName}`.trimEnd();
                draft[name].dtlAddr = '';
                draft[name].bldgCd = buildingCode || '';
              }),
            );
            setShowPostcode(false);
          }}
          submitMode={false}
        />
      </div>
    )
  );
};

const Address = ({
  children,
  title,
  name,
  addrSectionRef,
  onClickAddress,
  showPostcode,
  setShowPostcode,
  postcodeRef,
  onChangeAddress,
}) => {
  return (
    <div className='info-item'>
      <div className='info-item'>
        <h3 className='title'>{title}</h3>
        <div className='address-input-wrap'>
          <BasicAddress
            addrSectionRef={addrSectionRef}
            onClickAddress={() => onClickAddress(setShowPostcode, addrSectionRef)}
            name={name}
          />
          <DaumPostcode
            showPostcode={showPostcode}
            setShowPostcode={setShowPostcode}
            postcodeRef={postcodeRef}
            name={name}
          />
          <DetailAddress name={name} onChangeAddress={onChangeAddress} />
          {children}
        </div>
      </div>
    </div>
  );
};

export default withNamespace(Field, { NameAndBirth, TelContainer, AddressContainer });
